import { create } from 'zustand';

import type { CreateGuardianPlayerRequest } from '@/features/guardian/services/create-guardian-player';

/** A player the guardian added during onboarding but hasn't saved to the API yet. */
export type PendingPlayer = {
  /** Local-only id for list keys + removal (not the server id). */
  id: string;
  /** Ready-to-send body for POST /api/v1/guardians/me/players. */
  payload: CreateGuardianPlayerRequest;
  /** Whether the guardian reused their own address ("Same address with me"). The
   *  API payload doesn't carry this flag, so we keep it here to restore the edit
   *  sheet's checkbox. */
  sameAddress: boolean;
};

type State = {
  pendingPlayers: PendingPlayer[];
  /** Monotonic counter backing the local ids (avoids Date.now collisions). */
  seq: number;
};

type Actions = {
  /** Queue a validated payload; it is created later when step 2 "Continue" is pressed. */
  addPendingPlayer: (payload: CreateGuardianPlayerRequest, sameAddress: boolean) => void;
  /** Replace a queued player's payload in place (edit sheet save). */
  updatePendingPlayer: (
    id: string,
    payload: CreateGuardianPlayerRequest,
    sameAddress: boolean,
  ) => void;
  /** Drop one queued player (e.g. once it has been created on the server). */
  removePendingPlayer: (id: string) => void;
  /** Forget every queued player (on skip, or after they have all been created). */
  clearPendingPlayers: () => void;
};

/**
 * Holds the players a guardian adds on onboarding step 2 before they are saved.
 * The add-player sheet only validates + queues; the wizard's "Continue" then
 * creates them all. In-memory only — resets on app restart, so no stale state.
 */
export const useOnboardingPlayersStore = create<State & Actions>(set => ({
  pendingPlayers: [],
  seq: 0,
  addPendingPlayer: (payload, sameAddress) =>
    set(state => {
      const seq = state.seq + 1;
      return {
        seq,
        pendingPlayers: [...state.pendingPlayers, { id: `pending-${seq}`, payload, sameAddress }],
      };
    }),
  updatePendingPlayer: (id, payload, sameAddress) =>
    set(state => ({
      pendingPlayers: state.pendingPlayers.map(p =>
        p.id === id ? { ...p, payload, sameAddress } : p,
      ),
    })),
  removePendingPlayer: id =>
    set(state => ({ pendingPlayers: state.pendingPlayers.filter(p => p.id !== id) })),
  clearPendingPlayers: () => set({ pendingPlayers: [] }),
}));

export default useOnboardingPlayersStore;
